import React, { useEffect, useRef, useState } from "react";

import CartaEmDestaque from "./CartaEmDestaque";
import Confete, { DURACAO_DO_CONFETE_MS } from "./Confete";
import { variaveisDaRegiao } from "./estiloDaRegiao";
import FalaDoMascote from "./FalaDoMascote";
import { useInclinacao3d } from "./inclinacao3d";
import { useNavegacaoDaPista } from "./navegacaoDaPista";
import SetaDeslizante from "./SetaDeslizante";
import { prefereMenosMovimento, useTextoDigitado } from "./textoDigitado";

import "../styles/encerramentoTrilha.css";

/* As falas de reserva, para a região que não trouxer as suas.
   Sem bioma e sem NENHUM adjetivo referido a quem fala — é a única forma de um texto
   do núcleo caber na boca de um mascote de qualquer gênero, e o núcleo não conhece
   bioma nenhum por contrato. O contrato exige os campos da região, então isto é a
   rede para o caso de alguém plugar o módulo noutro host sem eles. */
const FALA_PADRAO =
  "Olha o que você descobriu nesta trilha. Foi muito bom explorar tudo isso com você.";
const FALA_PADRAO_SEM_CARTAS =
  "Você percorreu esta trilha do começo ao fim comigo. Foi muito bom ter você por aqui.";

/* Respiro entre o fim da fala e a saída do mascote. Existe para o olho fechar a
   leitura da última palavra — é o mesmo valor e o mesmo motivo do convite da
   cerimônia (`atrasoDaInstrucao`, em CerimoniaCard). Sob movimento reduzido a fala
   já nasce completa, e esperar quase um segundo olhando para uma tela parada não
   comunica nada. */
function atrasoDaSaida() {
  return prefereMenosMovimento() ? 0 : 900;
}

/* Quanto o mascote leva para sair de cena. Casado com `tl-anim-sai-para-cima` no
   CSS: se os dois divergirem, ou a figura some antes de terminar de sair, ou fica
   um vão em branco esperando um estado que já mudou. */
function duracaoDaSaida() {
  return prefereMenosMovimento() ? 0 : 420;
}

/* Passo das setas da vitrine, em px. É uma carta larga mais o vão — o suficiente
   para a seta sempre trazer uma carta nova à cena sem pular nenhuma, mesmo quando a
   carta está no teto de tamanho. */
const PASSO_DA_VITRINE = 300;

/**
 * Fim da trilha: o mascote fecha a jornada e o aluno vê o álbum dos cards que
 * descobriu.
 *
 * Componente de APRESENTAÇÃO — recebe os cards já resolvidos e não sabe de onde
 * vieram (quem deriva é `nucleo/album.js`, quem liga é `ava/TrilhaDeAtividade`).
 *
 * Não há botão de "próxima região": quem libera a trilha seguinte é o professor,
 * fora daqui — a tela só precisa dizer isso (decisão 29 do backlog do protótipo).
 * Um botão que não faz nada seria pior que nenhum.
 *
 * @param {object}   regiao      { nome, mascote: { nome, corpo } }
 * @param {Array}    cards       [{ id, nome, asset }] já conquistados
 * @param {Function} onFechar    volta para o tabuleiro
 */
function Encerramento({ regiao, cards = [], onFechar }) {
  const total = cards.length;

  const mascote = regiao.mascote;
  const fala =
    total > 0
      ? (mascote && mascote.falaEncerramento) || FALA_PADRAO
      : (mascote && mascote.falaEncerramentoSemCartas) ||
        FALA_PADRAO_SEM_CARTAS;
  const { digitado, digitando, completar } = useTextoDigitado(fala);

  /* A TELA TEM DUAS ETAPAS, e é isso que dá espaço às cartas.
     `falando` -> `saindo` -> `album`.

     O mascote fala primeiro, sozinho na tela; quando termina, sai de cena e o álbum
     fica com a altura inteira. Medido antes desta mudança: com a fala permanente, o
     cromo somava 543px — dos quais 259 eram só o mascote e a bolha — e o álbum era
     empurrado para fora da tela em 1366x768, obrigando a rolar para ver o prêmio.
     Com ele fora, sobra o dobro de altura e a carta cresce em vez de encolher.

     Sem cartas não há segunda etapa: a fala fica, porque é tudo o que a tela tem
     para dizer. */
  const [etapa, setEtapa] = useState("falando");

  useEffect(() => {
    if (total === 0 || etapa !== "falando" || digitando) return undefined;
    const id = setTimeout(() => setEtapa("saindo"), atrasoDaSaida());
    return () => clearTimeout(id);
  }, [total, etapa, digitando]);

  useEffect(() => {
    if (etapa !== "saindo") return undefined;
    const id = setTimeout(() => setEtapa("album"), duracaoDaSaida());
    return () => clearTimeout(id);
  }, [etapa]);

  const mostrandoFala = etapa !== "album";

  /* O MOMENTO DE CHEGADA. Até 05/08/2026 a tela final era mais calada que uma
     revelação do meio do caminho: a cerimônia tem confete e baú estourando em luz, e
     o fim da trilha inteira não tinha nada. O confete é o mesmo da cerimônia, cai
     uma vez, na chegada.

     Desmontado ao fim da queda em vez de ficar no DOM: são 18 elementos que não
     servem mais para nada, e um deles ainda por cima com animação declarada.

     Nada disso sob `prefers-reduced-motion` — confete é movimento grande e periférico,
     exatamente o que a preferência pede para evitar. Aqui a decisão é do JSX porque a
     regra global do módulo só zera a DURAÇÃO: as peças continuariam existindo,
     empilhadas no topo da tela. */
  const [confete, setConfete] = useState(() => !prefereMenosMovimento());

  useEffect(() => {
    if (!confete) return undefined;
    const id = setTimeout(() => setConfete(false), DURACAO_DO_CONFETE_MS);
    return () => clearTimeout(id);
  }, [confete]);

  /* A VITRINE ROLA NA HORIZONTAL, com as mesmas camadas da pista do tabuleiro:
     scroll nativo por baixo (é ele que dá teclado e leitor de tela), setas por cima
     (é a camada descobrível) e arrastar com o mouse. O hook é o mesmo — ele só
     precisa de um elemento rolável e de um passo. */
  const vitrineRef = useRef(null);
  const navegacao = useNavegacaoDaPista(vitrineRef, PASSO_DA_VITRINE);

  // As setas só sabem se há para onde ir depois que a vitrine existe e tem largura,
  // e ela só existe na segunda etapa.
  useEffect(() => {
    if (etapa === "album") navegacao.medirAlcance();
  }, [etapa, navegacao, total]);

  /* A carta sob o cursor inclina em 3D. Desligado enquanto a faixa está sendo
     ARRASTADA: os dois gestos usam `pointermove`, e uma carta girando embaixo da mão
     que puxa a fileira faz o arrasto parecer que travou. */
  useInclinacao3d(
    vitrineRef,
    ".encerramento__card-botao",
    etapa === "album" && !navegacao.arrastando
  );

  /* A carta aberta em destaque. Guarda o ID e não o objeto: assim, se a lista mudar
     (o servidor sincroniza o progresso enquanto a tela está aberta), o destaque
     acompanha a carta certa em vez de segurar uma cópia velha. */
  const [idEmDestaque, setIdEmDestaque] = useState(null);
  const cartaEmDestaque = cards.find(card => card.id === idEmDestaque) || null;

  return (
    // O `style` pelo mesmo motivo do palco do onboarding: este div tem a classe
    // `trilha-ludica`, e o CSS declara os tokens da região nela — declaração no
    // próprio elemento vence valor herdado do pai. Sem ele, a tela que mostra as
    // cartas conquistadas ficava com o fundo da Caatinga em qualquer região.
    <div
      className="trilha-ludica trilha-ludica__palco"
      data-tl-region={regiao.id}
      // `data-tl-etapa` é o gancho da regra que fecha o bloco contêiner do véu
      // (`::before` com `scale(1.08)`) em tela cheia — ver o comentário no
      // encerramentoTrilha.css. As outras duas etapas do palco já escreviam o
      // atributo (`Onboarding.jsx`) e por isso já estavam protegidas; esta não,
      // e era daí que vinham as barras de rolagem nos dois eixos.
      data-tl-etapa="encerramento"
      style={variaveisDaRegiao(regiao)}
    >
      <div className="encerramento tl-anim-fade-in">
        {confete && <Confete />}

        {cartaEmDestaque && (
          <CartaEmDestaque
            card={cartaEmDestaque}
            onFechar={() => setIdEmDestaque(null)}
          />
        )}

        <h2 className="encerramento__titulo">
          Trilha do {regiao.nome} concluída!
        </h2>

        {/* PRIMEIRA ETAPA: o mascote fala, sozinho na tela.

            A fala SUBSTITUI o parágrafo de sistema que vivia aqui ("Você descobriu 6
            cards explorando o Norte. Parabéns!"). Não soma: o personagem estava mudo
            no lugar mais nobre da tela enquanto uma voz sem dono dizia o que ele
            deveria estar dizendo.

            `cabeca` e não `corpo`: é o enquadramento fechado, feito para os slots em
            que o mascote divide espaço — e aqui ele divide com a bolha e, logo
            depois, sai para dar a tela ao álbum. O corpo inteiro fazia da figura o
            elemento mais alto da tela, que é justamente o que empurrava as cartas
            para fora dela. */}
        {mostrandoFala && (
          <div
            className={`encerramento__ato${
              etapa === "saindo" ? " encerramento__ato--saindo" : ""
            }`}
          >
            <FalaDoMascote
              mascote={mascote}
              figura={mascote && mascote.cabeca}
              texto={fala}
              digitado={digitado}
              digitando={digitando}
            />

            {/* Pular a digitação, e é um BOTÃO de verdade porque precisa funcionar
                no teclado. A fala mais longa do acervo tem 122 caracteres, que a
                45ms cada dão 5,5s — acima do limite de 5s da WCAG 2.2.2 para
                conteúdo que se atualiza sozinho, e o critério é atendido justamente
                por existir um jeito de parar. Na cerimônia esse papel é do toque no
                painel, que lá tem um `<button>` focável por trás; aqui não havia
                nenhum.

                Some quando a fala termina: um botão que não faz mais nada é ruído. */}
            {digitando && (
              <button
                type="button"
                className="encerramento__pular"
                onClick={completar}
              >
                Pular
              </button>
            )}
          </div>
        )}

        {/* NÃO HÁ CONTAGEM NA TELA, e ela já esteve aqui duas vezes.

            Primeiro como parágrafo de sistema ("Você descobriu 6 cards explorando o
            Norte"), depois como cromo acima da vitrine ("Você descobriu 6 cartas").
            As duas saíram pelo mesmo motivo: a informação já está na tela, em
            tamanho grande e colorido — são as cartas. Contá-las em texto é legenda
            de uma imagem que a criança está justamente olhando, e no primeiro ato
            ainda roubava a cena do personagem, que era o único que devia estar
            falando ali.

            Se um dia a contagem voltar, o lugar é o segundo ato e não o primeiro. */}
        {/* SEGUNDA ETAPA: o mascote saiu e as cartas ficam com a tela.

            UMA FILEIRA que rola na HORIZONTAL, e não uma grade que cresce para
            baixo. A razão é medida: com grade, 6 cartas em 1366x768 pediam +356px de
            rolagem vertical, e 22 cartas pediam +1415 — a tela do prêmio obrigando a
            rolar para ver o prêmio. Numa faixa horizontal a altura é sempre a de UMA
            carta, então o número de cartas deixa de disputar espaço com o resto da
            tela: a trilha de 22 cartas ocupa a mesma altura que a de 2.

            As camadas de navegação são as mesmas da pista do tabuleiro, e pela mesma
            razão de lá: o scroll nativo por baixo (é ele que dá teclado e leitor de
            tela), as setas por cima (é a camada descobrível) e o arrastar com o
            mouse. Nenhuma pode ser a única. */}
        {total > 0 && etapa === "album" && (
          <div className="encerramento__vitrine tl-anim-fade-in">
            <SetaDeslizante
              direcao={-1}
              visivel={navegacao.alcance.voltar}
              onClick={() => navegacao.deslizar(-1)}
              rotulo="Ver as cartas anteriores"
              testid="encerramento-seta-voltar"
            />

            <ul
              className={`encerramento__cards${
                navegacao.arrastando ? " encerramento__cards--arrastando" : ""
              }`}
              ref={vitrineRef}
              /* Container rolável precisa de NOME e de FOCO, senão é um beco para
                 quem navega por teclado: as cartas não são focáveis (não há o que
                 fazer com elas), então sem `tabindex` não há como rolar sem mouse. É
                 a mesma regra que `Tabuleiro.jsx` aplica à pista.

                 Só que ali a regra vem com `role="region"`, e aqui isso seria um
                 erro: `role` SUBSTITUI o papel implícito, e um `<ul role="region">`
                 deixa de ser uma lista — o leitor de tela para de anunciar "lista com
                 6 itens", que é exatamente a informação que o aluno quer nesta tela.
                 O `aria-label` sozinho já dá o nome, sem custo nenhum. Custou um
                 teste vermelho (`getByRole("list")`) para aparecer. */
              aria-label="Cartas que você descobriu"
              tabIndex={0}
              {...navegacao.ligacoes}
            >
              {cards.map((card, indice) => (
                <li
                  key={card.id}
                  className={`encerramento__card${
                    card.tipo === "raro" ? " encerramento__card--raro" : ""
                  }`}
                  // O brilho holográfico corre em cascata, não pisca tudo junto. O
                  // atraso vem daqui e não de `nth-child` (como no protótipo, que
                  // tinha 6 cards fixos): no AVA a quantidade depende da atividade.
                  //
                  // Zerado sob `prefers-reduced-motion` PELO JSX, e não pela folha:
                  // o bloco de `trilhaLudica.css` zera duração e iterações, mas não
                  // zera `animation-delay` — e ainda que zerasse, uma custom
                  // property inline venceria a folha de qualquer jeito.
                  style={{
                    "--tl-album-atraso": prefereMenosMovimento()
                      ? "0s"
                      : `${(indice % 6) * 0.35}s`
                  }}
                >
                  {/* A CARTA É UM BOTÃO, e não uma figura parada. Ela cresceu de 88
                      para 300px e continuava morta: o aluno tocava e nada acontecia,
                      embora o texto que explica cada uma já estivesse no dado
                      (`card.pilula`, o mesmo que o mascote fala na cerimônia).

                      `<button>` de verdade e não um div com onClick: assim o Tab
                      alcança cada carta, Enter e Espaço abrem, e o leitor de tela
                      anuncia o que o toque faz. O rótulo diz o nome E a ação, porque
                      "Queimadas" sozinho não avisa que há algo para ler. */}
                  <button
                    type="button"
                    className="encerramento__card-botao"
                    onClick={() => setIdEmDestaque(card.id)}
                    aria-label={`${card.nome} — ver a carta de perto`}
                  >
                    <span className="encerramento__card-moldura">
                      {/* `width`/`height` são os do arquivo (todas as cartas das duas
                        regiões são 420x588). Servem para o navegador reservar a
                        caixa na proporção certa antes de a imagem chegar — e para a
                        fileira não desalinhar se um asset faltar, caso em que hoje
                        a caixa colapsa a zero e sobra um nome órfão. */}
                      {/* `draggable={false}` porque o navegador tem um arrasto
                          PRÓPRIO para imagens, e ele ganha do nosso: apertar sobre a
                          arte disparava o drag-and-drop nativo (o fantasma da figura
                          colado no cursor) em vez de puxar a faixa. O gesto só
                          funcionava sobre a placa do nome, que não é imagem — foi
                          exatamente o sintoma relatado. */}
                      <img
                        src={card.asset}
                        alt=""
                        width="420"
                        height="588"
                        draggable={false}
                      />
                    </span>
                  </button>
                  <span className="encerramento__card-nome">{card.nome}</span>
                </li>
              ))}
            </ul>

            <SetaDeslizante
              direcao={1}
              visivel={navegacao.alcance.avancar}
              onClick={() => navegacao.deslizar(1)}
              rotulo="Ver as próximas cartas"
              testid="encerramento-seta-avancar"
            />
          </div>
        )}

        {/* O RODAPÉ É DO SEGUNDO ATO, e some enquanto o mascote fala.

            Os dois são a saída da tela — o recado do calendário do professor e o
            botão que leva de volta. Presentes durante a fala, eles competem com ela:
            o aluno acabou de terminar a trilha, o personagem está falando com ele, e
            embaixo já há um botão convidando a ir embora. Pior no caso de quem lê
            devagar, que é justamente o público.

            Enquanto não há cartas, porém, não existe segundo ato: a fala é tudo o
            que a tela tem, e sem o rodapé não haveria como sair dela. */}
        {(etapa === "album" || total === 0) && (
          <>
            <p className="encerramento__proxima">
              Sua próxima trilha aparece quando seu professor liberar.
            </p>

            <button type="button" className="tl-btn" onClick={onFechar}>
              Ver minha trilha
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default Encerramento;
